import { useEffect, useState } from "react";
import {
  defaultEditableQuestions,
  fetchAdminQuestions,
  saveAdminQuestions,
  type EditableQuestion,
} from "@/lib/formQuestions";

export default function AdminFormulario() {
  const [questions, setQuestions] = useState<EditableQuestion[]>(defaultEditableQuestions);
  const [questionsMsg, setQuestionsMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(
    null,
  );
  const [questionsUpdatedAt, setQuestionsUpdatedAt] = useState<string | null>(null);
  const [savingQuestions, setSavingQuestions] = useState(false);

  useEffect(() => {
    fetchAdminQuestions()
      .then((res) => {
        if (res.questions?.length) setQuestions(res.questions);
        setQuestionsUpdatedAt(res.updated_at);
      })
      .catch(() => {
        setQuestionsMsg({ kind: "err", text: "Nao conseguimos carregar as perguntas salvas." });
      });
  }, []);

  const patchQuestion = (index: number, patch: Partial<EditableQuestion>) => {
    setQuestions((arr) =>
      arr.map((q, i) => {
        if (i !== index) return q;
        const next = { ...q, ...patch } as EditableQuestion;
        if (next.kind === "choice") {
          return { ...next, options: "options" in next ? next.options : ["Sim", "Nao"] };
        }
        return next;
      }),
    );
  };

  const changeQuestionKind = (index: number, kind: EditableQuestion["kind"]) => {
    setQuestions((arr) =>
      arr.map((q, i) => {
        if (i !== index) return q;
        if (kind === "choice") {
          return {
            kind,
            text: q.text,
            hint: q.hint,
            options: q.kind === "choice" && q.options.length >= 2 ? q.options : ["Opcao 1", "Opcao 2"],
          };
        }
        return { kind, text: q.text, hint: q.hint };
      }),
    );
  };

  const addQuestion = () => {
    setQuestions((arr) => [...arr, { kind: "text", text: "Nova pergunta", hint: "" }]);
  };

  const removeQuestion = (index: number) => {
    setQuestions((arr) => arr.filter((_, i) => i !== index));
  };

  const moveQuestion = (index: number, dir: -1 | 1) => {
    setQuestions((arr) => {
      const next = [...arr];
      const to = index + dir;
      if (to < 0 || to >= next.length) return arr;
      const current = next[index];
      const target = next[to];
      if (!current || !target) return arr;
      next[index] = target;
      next[to] = current;
      return next;
    });
  };

  const patchOptions = (index: number, raw: string) => {
    const options = raw
      .split("\n")
      .map((x) => x.trim())
      .filter(Boolean);
    patchQuestion(index, { kind: "choice", options } as Partial<EditableQuestion>);
  };

  const restoreDefaultQuestions = () => {
    setQuestions(defaultEditableQuestions);
    setQuestionsMsg({ kind: "ok", text: "Perguntas padrao restauradas na tela. Clique em salvar para publicar." });
  };

  const saveQuestions = async () => {
    setQuestionsMsg(null);
    if (questions.length < 1) {
      setQuestionsMsg({ kind: "err", text: "Mantenha pelo menos uma pergunta." });
      return;
    }
    for (const q of questions) {
      if (q.text.trim().length < 3) {
        setQuestionsMsg({ kind: "err", text: "Toda pergunta precisa ter texto." });
        return;
      }
      if (q.kind === "choice" && q.options.filter(Boolean).length < 2) {
        setQuestionsMsg({ kind: "err", text: "Perguntas de escolha precisam ter pelo menos 2 opcoes." });
        return;
      }
    }

    setSavingQuestions(true);
    try {
      await saveAdminQuestions(questions);
      setQuestionsMsg({ kind: "ok", text: "Perguntas publicadas no formulario." });
      setQuestionsUpdatedAt(new Date().toISOString());
    } catch {
      setQuestionsMsg({ kind: "err", text: "Erro ao salvar perguntas." });
    } finally {
      setSavingQuestions(false);
    }
  };

  return (
    <div className="adm-form-page">
      <div className="adm-topbar">
        <div>
          <h1 className="adm-title">Formulario</h1>
          <p className="adm-sub">Edite as perguntas da Avaliacao Diagnostica.</p>
        </div>
      </div>

      <div className="adm-panel adm-form-editor">
        <div className="adm-panel-head">
          <div>
            <h2 className="adm-panel-title">Perguntas do formulario</h2>
            <p className="adm-sub">
              Edite as perguntas que aparecem na etapa "Sua historia".
              {questionsUpdatedAt && ` Ultima publicacao: ${questionsUpdatedAt}.`}
            </p>
          </div>
          <div className="adm-topactions">
            <button type="button" className="btn btn-ghost adm-mini-btn" onClick={restoreDefaultQuestions}>
              Restaurar padrao
            </button>
            <button type="button" className="btn btn-primary adm-mini-btn" onClick={saveQuestions} disabled={savingQuestions}>
              {savingQuestions ? "Salvando..." : "Salvar perguntas"}
            </button>
          </div>
        </div>

        {questionsMsg && (
          <div
            className="adm-login-error"
            style={
              questionsMsg.kind === "ok"
                ? {
                    background: "oklch(0.56 0.115 158 / .12)",
                    borderColor: "oklch(0.56 0.115 158 / .35)",
                    color: "var(--green)",
                  }
                : undefined
            }
          >
            {questionsMsg.text}
          </div>
        )}

        <div className="adm-question-list">
          {questions.map((q, i) => (
            <div className="adm-question-card" key={`${i}-${q.kind}`}>
              <div className="adm-question-head">
                <span className="adm-question-num">{String(i + 1).padStart(2, "0")}</span>
                <select
                  className="adm-select"
                  value={q.kind}
                  onChange={(e) => changeQuestionKind(i, e.target.value as EditableQuestion["kind"])}
                >
                  <option value="text">Resposta aberta</option>
                  <option value="choice">Escolha</option>
                  <option value="scale">Escala 1 a 10</option>
                </select>
                <div className="adm-question-actions">
                  <button type="button" className="adm-status-btn" onClick={() => moveQuestion(i, -1)} disabled={i === 0}>
                    Subir
                  </button>
                  <button
                    type="button"
                    className="adm-status-btn"
                    onClick={() => moveQuestion(i, 1)}
                    disabled={i === questions.length - 1}
                  >
                    Descer
                  </button>
                  <button
                    type="button"
                    className="adm-status-btn danger"
                    onClick={() => removeQuestion(i)}
                    disabled={questions.length === 1}
                  >
                    Remover
                  </button>
                </div>
              </div>

              <label className="adm-field-block">
                <span>Pergunta</span>
                <textarea
                  className="adm-textarea"
                  value={q.text}
                  onChange={(e) => patchQuestion(i, { text: e.target.value })}
                  rows={2}
                />
              </label>

              <label className="adm-field-block">
                <span>Dica abaixo da pergunta</span>
                <textarea
                  className="adm-textarea"
                  value={q.hint}
                  onChange={(e) => patchQuestion(i, { hint: e.target.value })}
                  rows={2}
                />
              </label>

              {q.kind === "choice" && (
                <label className="adm-field-block">
                  <span>Opcoes, uma por linha</span>
                  <textarea
                    className="adm-textarea"
                    value={q.options.join("\n")}
                    onChange={(e) => patchOptions(i, e.target.value)}
                    rows={Math.max(3, q.options.length)}
                  />
                </label>
              )}
            </div>
          ))}
        </div>

        <button type="button" className="btn btn-ghost adm-add-question" onClick={addQuestion}>
          Adicionar pergunta
        </button>
      </div>
    </div>
  );
}
